import type { Session } from '@fanduel/cpe-um-bundle/account/contract';

import { setUserSessionContext } from '@/store/actions';
import { getLocalAccountSession } from '@/utilities/getters/sessionContextGetters';
import { normalizeRegion } from '@/utilities/constants/regions';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';

const formatSessionTimestamp = (value: Date | string | number | undefined) => {
  if (value === undefined) {
    return undefined;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  return String(value);
};

type BlobDriftContext = {
  authTokenMismatch: boolean;
  sessionIdMismatch: boolean;
  userIdMismatch: boolean;
  umSessionId: string;
  blobSessionId?: string;
};

const getBlobDriftContext = (
  session: Session,
  blob: ReturnType<typeof getLocalAccountSession>,
): BlobDriftContext | null => {
  if (!blob) {
    return null;
  }

  const authTokenMismatch = blob.authToken.jwt !== session.token;
  const sessionIdMismatch = blob.sessionId !== session.sessionId;
  const umUserId = session.userId ? String(session.userId) : undefined;
  const userIdMismatch = Boolean(
    umUserId && blob.userId && blob.userId !== umUserId,
  );

  if (!authTokenMismatch && !sessionIdMismatch && !userIdMismatch) {
    return null;
  }

  return {
    authTokenMismatch,
    sessionIdMismatch,
    userIdMismatch,
    umSessionId: session.sessionId,
    blobSessionId: blob.sessionId,
  };
};

/**
 * Applies Account UM subscribe Session to Zustand first, then enriches login/region
 * from the refreshable blob when available.
 */
export const applyUmSessionToStore = (session: Session): boolean => {
  const blob = getLocalAccountSession();
  const drift = getBlobDriftContext(session, blob);

  if (drift) {
    Logger.logRumAction({
      name: '[GW][SESSION] Refreshable blob drift from subscribe session',
      description:
        'x-account-session / localStorage lagged behind Account UM subscribe payload.',
      context: drift,
      useCase: DdEventCategory.Session,
      eventType: DdEventType.Info,
    });
  }

  const regionFromSession = session.region
    ? normalizeRegion(String(session.region))
    : undefined;

  setUserSessionContext({
    authToken: {
      jwt: session.token,
      ...(session.created
        ? { created: new Date(formatSessionTimestamp(session.created) ?? '') }
        : {}),
      ...(session.expires
        ? { expired: new Date(formatSessionTimestamp(session.expires) ?? '') }
        : {}),
    },
    sessionId: session.sessionId,
    userId: session.userId ? String(session.userId) : (blob?.userId ?? null),
    region: regionFromSession ?? blob?.region ?? null,
    ...(blob?.loginToken ? { loginToken: blob.loginToken } : {}),
    hasActiveSession: true,
    ...(session.created
      ? { created: new Date(formatSessionTimestamp(session.created) ?? '') }
      : {}),
  });

  return true;
};

/**
 * Syncs Account UM-managed refreshable storage to the local Zustand store.
 */
export const syncSessionToStore = () => {
  const sessionContext = getLocalAccountSession();
  if (!sessionContext) return false;

  setUserSessionContext({
    ...sessionContext,
    hasActiveSession: true,
  });

  return true;
};
